import type { User } from 'firebase/auth'

export type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; user: User }
  | { status: 'error'; detail: string }

type Listener = (state: AuthState) => void

function createStore() {
  let state: AuthState = { status: 'loading' }
  const listeners = new Set<Listener>()

  return {
    snapshot: () => state,
    set(next: AuthState) {
      state = next
      for (const l of listeners) l(state)
    },
    subscribe(l: Listener) {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    waitUntilSettled(): Promise<AuthState> {
      if (state.status !== 'loading') return Promise.resolve(state)
      return new Promise((resolve) => {
        const unsub = this.subscribe((s) => {
          if (s.status !== 'loading') { unsub(); resolve(s) }
        })
      })
    },
    /**
     * Resolves true as soon as `uid` is the signed-in user in the store, and
     * false if that has not happened within `timeoutMs`.
     *
     * Two gaps, and the uid closes the second one. `signInWithCustomToken`
     * resolves before the provider's `onAuthStateChanged` listener has run, so
     * a caller that navigates immediately hands the router a store that still
     * says signed-out and the route guard bounces the user back to sign-in.
     * And when a session for ANOTHER account is still cached, waiting for
     * "any signed-in user" is already satisfied: the caller would navigate
     * before the swap and show the app as the previous user.
     */
    waitUntilSignedInAs(uid: string, timeoutMs: number): Promise<boolean> {
      if (state.status === 'signed-in' && state.user.uid === uid) return Promise.resolve(true)
      return new Promise((resolve) => {
        let timer: ReturnType<typeof setTimeout>
        let unsub: (() => void) | undefined
        const settle = (value: boolean) => {
          clearTimeout(timer)
          unsub?.()
          resolve(value)
        }
        timer = setTimeout(() => settle(false), timeoutMs)
        unsub = this.subscribe((s) => {
          if (s.status === 'signed-in' && s.user.uid === uid) settle(true)
        })
      })
    },
    reset() {
      state = { status: 'loading' }
      listeners.clear()
    },
  }
}

export const authStore = createStore()
